import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Container,
  createTheme,
  CssBaseline,
  List,
  ListItem,
  Toolbar,
  Typography,
  ThemeProvider,
} from "@mui/material";
import { styled } from "@mui/system";

// CUSTOM SETTINGS

// style
// Thème : 1 à 15 (voir le tableau du README du projet), ou "0" pour régler les
// valeurs à la main juste en dessous.
//
// MODIFICATION LOCALE : la ligne lisait `process.env.DEFAULT_THEME`, une variable
// d'environnement lue par le serveur de développement du projet. Sur un hébergement
// statique il n'y a aucun serveur : la valeur serait absente et le thème retomberait
// toujours sur le même. Le numéro est donc écrit ici, et c'est la seule ligne à
// changer pour changer de look.
const defaultTheme = "4";

// custom / default theme
let bookmarkHeight = "150";
let bookmarkPadding = "15";
let bookmarkRadius = "24";
let bookmarkWidth = "150";
let showNames = "1";

switch (defaultTheme) {
  case "1": // homepage
    bookmarkHeight = "150";
    bookmarkPadding = "15";
    bookmarkRadius = "24";
    bookmarkWidth = "150";
    showNames = "1";
    break;
  case "2": // homepage compact
    bookmarkHeight = "150";
    bookmarkPadding = "15";
    bookmarkRadius = "24";
    bookmarkWidth = "150";
    showNames = "0";
    break;
  case "3": // homepage super compact
    bookmarkHeight = "150";
    bookmarkPadding = "15";
    bookmarkRadius = "24";
    bookmarkWidth = "150";
    showNames = "0";
    break;
  case "4": // homepage - small
    bookmarkHeight = "100";
    bookmarkPadding = "15";
    bookmarkRadius = "24";
    bookmarkWidth = "100";
    showNames = "1";
    break;
  case "5": // homepage compact - small
    bookmarkHeight = "100";
    bookmarkPadding = "15";
    bookmarkRadius = "24";
    bookmarkWidth = "100";
    showNames = "0";
    break;
  case "6": // homepage super compact - small
    bookmarkHeight = "100";
    bookmarkPadding = "15";
    bookmarkRadius = "24";
    bookmarkWidth = "100";
    showNames = "0";
    break;

  case "7": // homepage - round
    bookmarkHeight = "150";
    bookmarkPadding = "15";
    bookmarkRadius = "75";
    bookmarkWidth = "150";
    showNames = "1";
    break;
  case "8": // homepage compact - round
    bookmarkHeight = "150";
    bookmarkPadding = "15";
    bookmarkRadius = "75";
    bookmarkWidth = "150";
    showNames = "0";
    break;
  case "9": // homepage super compact - round
    bookmarkHeight = "150";
    bookmarkPadding = "15";
    bookmarkRadius = "75";
    bookmarkWidth = "150";
    showNames = "0";
    break;
  case "10": // homepage - small - round
    bookmarkHeight = "100";
    bookmarkPadding = "15";
    bookmarkRadius = "50";
    bookmarkWidth = "100";
    showNames = "1";
    break;
  case "11": // homepage compact - small - round
    bookmarkHeight = "100";
    bookmarkPadding = "15";
    bookmarkRadius = "50";
    bookmarkWidth = "100";
    showNames = "0";
    break;
  case "12": // homepage super compact - small - round
    bookmarkHeight = "100";
    bookmarkPadding = "15";
    bookmarkRadius = "50";
    bookmarkWidth = "100";
    showNames = "0";
    break;
  case "13": // metro
    bookmarkHeight = "175";
    bookmarkPadding = "20";
    bookmarkRadius = "0";
    bookmarkWidth = "250";
    showNames = "0";
    break;
  case "14":
    bookmarkHeight = "200";
    bookmarkPadding = "10";
    bookmarkRadius = "24";
    bookmarkWidth = "200";
    showNames = "1";
    break;
  case "15": // widescreen
    bookmarkHeight = "125";
    bookmarkPadding = "20";
    bookmarkRadius = "4";
    bookmarkWidth = "225";
    showNames = "0";
    break;

  default: // custom
    break;
}

// Default theme
const theme = createTheme({
  palette: {
    mode: "dark",
  },
  typography: {
    fontFamily:
      "SF Pro Text, system-ui, -apple-system, BlinkMacSystemFont, Helvetica Neue, Helvetica, Arial, sans-serif",
  },
});

// Image de fond personnalisée.
// Dépose un seul fichier dans `app/public/background/` en le nommant
// `background.jpg` (ou `.jpeg`, `.png`, `.webp`), puis republie. Le premier
// fichier trouvé est utilisé ; s'il n'y en a aucun, le fond reste noir.
const BACKGROUND_CANDIDATES = [
  "/background/background.jpg",
  "/background/background.jpeg",
  "/background/background.png",
  "/background/background.webp",
];

// Container for the app
const RootContainer = styled("div")({
  backgroundColor: "black",
  backgroundPosition: "center center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  minHeight: "100vh",
});

// Container for the main content
const AppContainer = styled(Container)({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  margin: "0 !important",
  padding: "0 !important",
  width: "100%",
});

const BookmarkName = styled(Typography)({
  display: showNames === "true" || showNames === "1" ? "initial" : "none",
  // `&&` : sans cela, le style de `variant="subtitle1"` reprend la main et le nom
  // reste à 16 px, trop large pour une vignette de 100 px.
  "&&": {
    fontSize: "0.78rem",
    lineHeight: "1.25",
  },
  fontWeight: "bold",
  margin: "0",
  overflow: "hidden",
  textOverflow: "ellipsis",
  width: "100%",
  // Apply 25% smaller font size on mobile phones
  [`@media ((max-device-width: 844px))`]: {
    fontSize: "75%",
    textOverflow: "initial",
  },
});

// Container for the date and time display
const DateTimeContainer = styled(Box)({
  alignItems: "center",
  display: "flex",
});

// Container of the single, full-page grid: every bookmark, no category titles.
const BookmarksList = styled(List)({
  alignContent: "start",
  boxSizing: "border-box",
  display: "grid",
  gridTemplateColumns: `repeat(auto-fill, ${parseInt(bookmarkWidth) + parseInt(bookmarkPadding)}px)`,
  justifyContent: "center",
  margin: "0",
  padding: "8px 12px 24px",
  width: "100%",
});

const BookmarkContent = styled("div")({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  marginBottom: "8px",
  textAlign: "center",
  whiteSpace: "nowrap",
  width: `${parseInt(bookmarkWidth) + parseInt(bookmarkPadding)}px`,
  // Apply 50% smaller width and height on mobile phones
  [`@media ((max-device-width: 844px))`]: {
    marginBottom: "4px",
    width: `${parseInt(bookmarkWidth / 2) + parseInt(bookmarkPadding / 2)}px`,
  },
});

// Individual bookmark item
const BookmarkItem = styled(ListItem)({
  padding: "0px",
  width: "fit-content",
});

// Image for each bookmark
const BookmarkImage = styled("img")({
  borderRadius: `${bookmarkRadius}px`,
  height: `${bookmarkHeight}px`,
  marginBottom: "8px",
  width: `${bookmarkWidth}px`,
  // Apply 50% smaller width and height on mobile phones
  [`@media ((max-device-width: 844px))`]: {
    borderRadius: `${bookmarkRadius / 2}px`,
    marginBottom: "4px",
    width: `${bookmarkWidth / 2}px`,
    height: `${bookmarkHeight / 2}px`,
  },
});

// Les liens sont directs : un appui ouvre le site tout de suite, sans page
// intermédiaire (« GO TO SITE ») ni redirection.

// Logo de secours : si un favori n'a pas d'image (ou si son image ne charge pas),
// on prend le favicon du site, déduit de son adresse. Un lien ajouté avec
// seulement `name` et `url` reçoit donc une icône tout seul.
const faviconDeSecours = (url) => {
  try {
    return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=128`;
  } catch (error) {
    return "";
  }
};

function App() {
  const [title] = useState("Accueil Tesla");
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [bookmarks, setBookmarks] = useState([]);
  const [background, setBackground] = useState(null);
  const [error, setError] = useState(null); // State to hold the error message

  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const response = await fetch("/bookmarks.json");
        if (!response.ok) {
          throw new Error("Error fetching bookmarks");
        }
        const data = await response.json();
        // Liste plate attendue. Repli sur l'ancien format à catégories, pour ne
        // pas casser une liste non encore convertie.
        const list = Array.isArray(data.bookmarks)
          ? data.bookmarks
          : (data.categories || []).reduce(
              (acc, category) => acc.concat(category.bookmarks || []),
              []
            );
        setBookmarks(list);
        setError(null); // Reset the error state if the fetch is successful
      } catch (error) {
        setError(error.message || "Unknown error occurred");
      }
    };

    fetchBookmarks();
  }, []);

  // Image de fond : on garde le premier fichier présent dans /background/.
  useEffect(() => {
    let abandonne = false;

    const essayer = (index) => {
      if (abandonne || index >= BACKGROUND_CANDIDATES.length) {
        return;
      }
      const image = new Image();
      image.onload = () => {
        if (!abandonne) {
          setBackground(BACKGROUND_CANDIDATES[index]);
        }
      };
      image.onerror = () => essayer(index + 1);
      image.src = BACKGROUND_CANDIDATES[index];
    };

    essayer(0);

    return () => {
      abandonne = true;
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const formattedDate = currentDateTime.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const formattedTime = currentDateTime.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });

  useEffect(() => {
    document.title = title;
  }, [title]);

  // Un voile sombre sur l'image garde les vignettes et les noms lisibles.
  const backgroundStyle = background
    ? {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url("${background}")`,
      }
    : {};

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RootContainer style={backgroundStyle}>
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: background ? "transparent" : "#000000",
            color: "#ECECEC",
            boxShadow: "none",
            backgroundImage: background
              ? "linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0))"
              : "none",
          }}
        >
          <Toolbar>
            <DateTimeContainer>
              <Typography
                variant="body1"
                style={{ marginRight: "8px", fontWeight: "bold" }}
              >
                {formattedDate}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: "bold" }}>
                {formattedTime}
              </Typography>
            </DateTimeContainer>
            {/* Espace souple : la date reste à gauche, comme avant. */}
            <Box sx={{ flex: 1 }} />
          </Toolbar>
        </AppBar>
        <Toolbar />
        <AppContainer maxWidth={false}>
          {error ? ( // Display the error message if it exists
            <Typography
              variant="h6"
              color="error"
              align="center"
              style={{ padding: "20px" }}
            >
              <span>
                PLEASE FIX THE FOLLOWING ERROR IN BOOKMARKS.JSON:
                <br />
                {error}
              </span>
            </Typography>
          ) : (
            <BookmarksList>
              {bookmarks.map((bookmark, index) => (
                <BookmarkItem
                  key={index}
                  button
                  component="a"
                  target="_self"
                  href={bookmark.url}
                >
                  <BookmarkContent>
                    <BookmarkImage
                      src={bookmark.image || faviconDeSecours(bookmark.url)}
                      alt={bookmark.name}
                      onError={(event) => {
                        const secours = faviconDeSecours(bookmark.url);
                        if (secours && event.currentTarget.src !== secours) {
                          event.currentTarget.src = secours;
                        }
                      }}
                      style={{
                        objectFit: "cover",
                        background: bookmark.background || "transparent",
                        border: bookmark.border || "none",
                      }}
                    />
                    <BookmarkName variant="subtitle1">
                      {bookmark.name}
                    </BookmarkName>
                  </BookmarkContent>
                </BookmarkItem>
              ))}
            </BookmarksList>
          )}
        </AppContainer>
      </RootContainer>
    </ThemeProvider>
  );
}

export default App;
